// @flow
import * as React from 'react';

import { selectTaxesDisplaySetting } from '@gandi/authentication';
import { useTranslator, mapTags } from '@gandi/translate';
import {
  Alert,
  Badge,
  Base,
  Button,
  TypoListItem as ListItem,
  OrderedList,
  Spinner,
  Svg,
} from '@gandi/ui';

import { useAppSelector } from 'src/hooks/useAppSelector';

import { Intro } from '../Layout';
import { useGetPrice } from '../helpers';

import { useTmchPrices } from './hooks/useTmchPrices';

import styles from './Overview.scss';

const Overview = (): React.Node => {
  const { prices, pricesFailure, pricesFetching, pricesLoaded } =
    useTmchPrices();
  const getPrice = useGetPrice();
  const translator = useTranslator();
  const { __, formatPrice } = translator;
  const displayTaxes = useAppSelector(selectTaxesDisplaySetting);
  const refer = Array.isArray(prices) && prices.find(e => e.min_duration === 1);

  return (
    <>
      <Intro title={__('TMCH Trademark Registration')} />

      {pricesFailure && !pricesFetching && (
        <Alert
          title={__('Unexpected error')}
          message={__('Unexpected error occurred. Please try again later.')}
          intent="danger"
        />
      )}

      {!pricesLoaded && !pricesFailure && prices.length === 0 && (
        <Spinner centered />
      )}

      {pricesLoaded && (
        <>
          <Base padding="small" marginBottom="medium" backgroundColor="surface">
            <Base flex tagName="h3">
              <Svg name="secure" theme="green" className={styles.svg} />
              {__(
                'Ask Gandi to register your trademark in the Trademark ClearingHouse database',
              )}
            </Base>

            <Base tagName="p">
              {mapTags(
                __(
                  'Simplify your Sunrise domain registration workflow by managing your SMD file at Gandi. We will handle ' +
                    'the complex part, so you can focus on your brand strategy. [[link:Learn more]].',
                ),
                ({ link }) => ({
                  link: (
                    <a
                      href={__(
                        'https://docs.gandi.net/en/domain_names/domain_services/tmch.html',
                      )}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {link}
                    </a>
                  ),
                }),
              )}
            </Base>

            <OrderedList marginBottom="base">
              <ListItem>
                <span>
                  {mapTags(
                    __(
                      'Download and fill the [[link:Declaration of proof of use template]]',
                    ),
                    ({ link }) => ({
                      link: (
                        <a
                          // XXX: Put the url in conf?
                          href="https://www.trademark-clearinghouse.com/sites/default/files/files/downloads/declaration_of_proof_of_use_-_tm_holder.pdf"
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                        >
                          {link}
                        </a>
                      ),
                    }),
                  )}
                </span>
              </ListItem>
              <ListItem>
                {__('Complete the form and provide the required documents')}
              </ListItem>
              <ListItem>
                {__(
                  'Submit & pay. You will then receive an email to confirm your request.',
                )}
              </ListItem>
            </OrderedList>

            <Button small to="/tmch/tmch-registration" grouped>
              {__('File a request')}
            </Button>
          </Base>

          <Base tagName="h2" size="bigger" align="center" margin="base">
            {__('How much does it cost?')}
          </Base>

          <Base padding="small" backgroundColor="surface">
            <Base tagName="p" marginTop="none">
              {__(
                'Prices vary depending on the trademark registration duration',
              )}
            </Base>

            {Array.isArray(prices) && prices.length > 0 && (
              <Base
                tagName="ul"
                paddingLeft="none"
                marginBottom="none"
                listStyleType="none"
              >
                {prices.map(price => (
                  <Base
                    key={price.min_duration}
                    className={styles.priceItem}
                    marginBottom="small"
                    tagName="li"
                  >
                    <Badge>
                      {__('%(count)s years', { count: price.max_duration })}
                    </Badge>
                    {price.min_duration !== 1 && refer && (
                      <Base tagName="del" marginLeft="xsmall">
                        {formatPrice(
                          getPrice(refer) * price.min_duration,
                          price.currency,
                        )}
                      </Base>
                    )}

                    <Base tagName="span" marginLeft="xsmall">
                      {formatPrice(
                        getPrice(price) * price.min_duration,
                        price.currency,
                      )}
                    </Base>
                    {price.taxes.length > 0 && (
                      <Base tagName="span" marginLeft="xsmall" color="light">
                        {displayTaxes ? __('incl. taxes') : __('excl. taxes')}
                      </Base>
                    )}
                  </Base>
                ))}
              </Base>
            )}
          </Base>
        </>
      )}
    </>
  );
};

export default Overview;
